import { LANDING_HEADER_COMPANY, LANDING_HEADER_PRODUCTS, LANDING_HEADER_PROMOTIONS, LANDING_HEADER_SUPPORT } from '@constant/header';
import { PATHS_BY_ENV } from '@constant/paths';
import { env } from '@helpers/EnvHelper';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	AppBar,
	Box,
	Button,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Toolbar,
	Typography,
	Link
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { withStyles } from '@mui/styles';
import { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
	menu: {
		top: '-3px',
		height: '87%',
		'& .MuiButtonBase-root': {
			padding: '0px'
		},
		'& .MuiPaper-root': {
			minWidth: '-webkit-fill-available',
			left: '0px !important'
		},
		'& ul.MuiList-padding': {
			padding: 0,
			height: '100%',
			width: '100%',
			display: 'flex !important',
			flexDirection: 'column',
			justifyContent: 'space-between !important'
		},
		'& .MuiMenu-paper': {
			height: '100%',
			display: 'flex !important',
			justifyContent: 'space-between !important'
		}
	},
	menuButton: {
		paddingLeft: 0,
		paddingRight: 0
	},
	accordion: {
		width: '100%',
		boxShadow: 'none',
		border: 'none',
		margin: 0,
		'&:hover': {
			backgroundColor: 'rgba(0, 0, 0,0.0)'
		},
		'& .Mui-expanded': {
			margin: 0,
			padding: 0
		},
		'& .MuiButtonBase-root': {
			padding: '0px'
		},
		'& .MuiSvgIcon-root': {
			color: 'black'
		}
	},
	accordionSummary: {
		border: 'none !important',
		width: 'auto !important',
		paddingLeft: 0,
		paddingRight: '24px !important',
		[theme.breakpoints.down('md')]: {
			width: '97%'
		},
		[theme.breakpoints.down('sm')]: {
			paddingRight: '16px !important',
			width: '96%'
		}
	},
	accordionDetails: {
		width: 'auto',
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		'& > a': {
			display: 'flex',
			padding: 8,
			flexDirection: 'column',
			width: '90%',
			borderRadius: 8,
			textDecoration: 'none',
			color: `${theme.palette.secondary.main} !important`,
			'&:hover': {
				'& .MuiTypography-h4': {
					color: theme.palette.secondary.main
				}
			}
		}
	},
	wrapAccordion: {
		overflowY: 'auto',
		WebkitOverflowScrolling: 'touch',
		scrollbarWidth: 'none',
		padding: '0px !important',
		'&::-webkit-scrollbar': {
			display: 'none'
		}
	},
	link: {
		textDecoration: 'none',
		color: 'black',
		'&:hover': {
			textDecoration: 'underline'
		}
	},
	darkAccordionSummary: {
		color: theme.surfaceVariants?.primary?.surface,
		backgroundColor: `${theme.surfaceVariants?.primary?.onSurface} !important`
	},
	darkMenu: {
		'& .MuiMenu-paper': {
			backgroundColor: theme.surfaceVariants?.primary?.onSurface,
			color: theme.surfaceVariants?.primary?.surface
		}
	},
	darkAccordion: {
		backgroundColor: theme.surfaceVariants?.primary?.onSurface,
		color: theme.surfaceVariants?.primary?.surface,
		'&:hover': {
			backgroundColor: '#29292C',
			color: theme.surfaceVariants?.primary?.surface
		},
		'& .MuiSvgIcon-root': {
			color: theme.surfaceVariants?.primary?.surface
		}
	},
	darkHeader: {
		backgroundColor: theme.surfaceVariants?.primary?.onSurface
	},
	darkHeaderBtn: {
		color: theme.surfaceVariants?.primary?.surface
	},
	darkLink: {
		color: `${theme.surfaceVariants?.primary?.surface} !important`
	},
	loginBtn: {
		border: `1px solid ${theme.surfaceVariants?.primary?.surface}`,
		color: `${theme.palette.text.primary} !important`
	},
	darkLoginBtn: {
		border: `1px solid ${theme.surfaceVariants?.primary?.surface}`,
		'&:hover': {
			border: `1px solid ${theme.surfaceVariants?.primary?.surface}`
		},
		'& > p': {
			color: `${theme.surfaceVariants?.primary?.surface} !important`
		}
	}
}));

const StyledToolbar = withStyles((theme: any) => ({
	root: {
		justifyContent: 'space-between',
		height: '56px',
		minHeight: '56px'
	}
}))(Toolbar);

const AccordionMenu = (props: any) => {
	const { menuText, dataSrc, isBusiness, closeMenu } = props;
	const { classes, cx } = useStyles();

	return (
		<MenuItem disableRipple>
			<Accordion
				className={cx(classes.accordion, {
					[classes.darkAccordion]: isBusiness
				})}>
				<AccordionSummary
					disableRipple
					className={cx(classes.accordionSummary, { [classes.darkAccordionSummary]: isBusiness })}
					expandIcon={<ExpandMoreIcon />}>
					<Typography padding="3px 1rem">{menuText}</Typography>
				</AccordionSummary>
				<AccordionDetails className={classes.accordionDetails}>
					{dataSrc?.map((item: any) => {
						return (
							<Box key={`${item?.title}`}>
								<Typography variant="h4">{item?.title}</Typography>
								{item?.components &&
									item?.components.map((comp, index) => (
										<Box key={index}>
											<Link
												href={`${PATHS_BY_ENV[env][comp?.link]}${comp?.propsLink ?? ''}`}
												onClick={closeMenu}
												className={cx(classes.link, { [classes.darkLink]: isBusiness })}>
												<Typography padding={1}>{comp?.title}</Typography>
											</Link>
										</Box>
									))}
								<Divider />
							</Box>
						);
					})}
				</AccordionDetails>
			</Accordion>
		</MenuItem>
	);
};

const MobileHeader = (props: any) => {
	const { mMLogo, isBusiness }: any = props || {};
	const { classes, cx } = useStyles();
	const appBarRef = useRef(null);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [isFixed, setIsFixed] = useState(false);
	const [isShowNavi, setIsShowNavi] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			const scrollPosition = window.scrollY;

			if (scrollPosition === 0) {
				setIsFixed(false);
			} else {
				setIsFixed(true);
			}
		};

		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleClick = () => {
		isShowNavi ? closeMenu() : openMenu();
	};

	const openMenu = () => {
		setAnchorEl(appBarRef.current);
		setIsShowNavi(true);
	};

	const closeMenu = () => {
		setAnchorEl(null);
		setIsShowNavi(false);
	};

	return (
		<AppBar
			position="fixed"
			sx={{
				boxShadow: isFixed ? '0 8px 24px rgb(0 0 0 / 13%)' : '',
				backdropFilter: isFixed ? 'blur(20px)' : 'blur(0px)',
				backgroundColor: isShowNavi ? 'white' : 'none'
			}}
			className={cx({
				[classes.darkHeader]: isBusiness
			})}
			color="inherit"
			ref={appBarRef}>
			<StyledToolbar>
				<Link href="/" style={{ textDecoration: 'none' }} color="inherit">
					<img src={mMLogo} alt="MoneyMatchLogo" width={160} height={40} />
				</Link>
				<IconButton
					className={cx(classes.menuButton, {
						[classes.darkHeaderBtn]: isBusiness
					})}
					color="inherit"
					aria-label="menu"
					onClick={handleClick}
					sx={{
						'&:hover': {
							background: 'none'
						},
						'&:active': {
							background: 'none'
						}
					}}>
					{isShowNavi ? <CloseIcon /> : <MenuIcon />}
				</IconButton>
			</StyledToolbar>
			<Menu
				className={cx(classes.menu, {
					[classes.darkMenu]: isBusiness
				})}
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={closeMenu}
				disableScrollLock={true}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
				<div className={classes.wrapAccordion}>
					<AccordionMenu isBusiness={isBusiness} menuText={'Products'} dataSrc={LANDING_HEADER_PRODUCTS} closeMenu={closeMenu} />
					<MenuItem>
						<Link
							className={cx(classes.link, { [classes.darkLink]: isBusiness })}
							href={PATHS_BY_ENV[env][LANDING_HEADER_PROMOTIONS.link]}
							onClick={closeMenu}>
							<Typography padding="1rem">{LANDING_HEADER_PROMOTIONS.title}</Typography>
						</Link>
					</MenuItem>
					<AccordionMenu isBusiness={isBusiness} menuText={'Company'} dataSrc={LANDING_HEADER_COMPANY} closeMenu={closeMenu} />
					<MenuItem>
						<Link
							className={cx(classes.link, { [classes.darkLink]: isBusiness })}
							href={PATHS_BY_ENV[env][LANDING_HEADER_SUPPORT.link]}
							onClick={closeMenu}>
							<Typography padding="1rem">{LANDING_HEADER_SUPPORT.title}</Typography>
						</Link>
					</MenuItem>
				</div>

				<Grid container direction="row" columnGap={2} justifyContent="center" marginBottom={2}>
					<Grid item xs={5}>
						<Button
							fullWidth
							variant="outlined"
							className={cx(classes.loginBtn, { [classes.darkLoginBtn]: isBusiness })}
							LinkComponent={Link}
							onClick={closeMenu}
							href={PATHS_BY_ENV[env].loginLink}>
							<Typography variant="body1" color="inherit">
								Login
							</Typography>
						</Button>
					</Grid>
					<Grid item xs={5}>
						<Button fullWidth variant="contained" color="primary" LinkComponent={Link} href={PATHS_BY_ENV[env].signupLink} onClick={closeMenu}>
							<Typography variant="body1">Sign Up</Typography>
						</Button>
					</Grid>
				</Grid>
			</Menu>
		</AppBar>
	);
};

export default MobileHeader;
